import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Zap, Plus, Loader2, AlertCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { employeeService } from '../../empleados/services/employeeService';
import {
  filterRequirementsByDate,
  formatRequirementDate,
  getCoverageMissingSkills,
  getLevelLabel,
  getPatternSkillRequirements,
  isCoverageFullyCovered,
} from '../utils/laborPatternSkillsUtils';

const LEVELS = ['básico', 'intermedio', 'avanzado', 'experto'];

/**
 * Requisitos de habilidades y comprobación de cobertura para un patrón (vista Patrón).
 */
export function LaborPatternSkillSetup({
  patternId,
  patternName,
  calendarId,
  year,
  onAfterChange,
}) {
  const queryClient = useQueryClient();
  const [coverageDate, setCoverageDate] = useState(new Date().toISOString().slice(0, 10));
  const [form, setForm] = useState({
    skillId: '',
    minLevel: 'básico',
    requiredCount: '1',
    validFrom: '',
    validTo: '',
    reason: '',
  });
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      validFrom: `${year}-01-01`,
      validTo: '',
      skillId: '',
    }));
    setFormError(null);
  }, [patternId, year]);

  const { data: patterns = [], isLoading: loadingPatterns } = useQuery({
    queryKey: ['labor-patterns-with-skills', calendarId],
    queryFn: () => laborScheduleService.listPatternsByCalendar(calendarId),
    enabled: Boolean(calendarId) && Boolean(patternId),
  });

  const { data: skillsCatalog = [], isLoading: loadingCatalog } = useQuery({
    queryKey: ['skills-catalog'],
    queryFn: () => employeeService.getSkillsCatalog(),
    staleTime: 120_000,
  });

  const pattern = patterns.find((p) => String(p.id) === String(patternId));
  const allRequirements = getPatternSkillRequirements(pattern);
  const activeRequirements = useMemo(
    () => filterRequirementsByDate(allRequirements, coverageDate),
    [allRequirements, coverageDate]
  );

  const {
    data: coverage,
    isLoading: loadingCoverage,
    isFetching: fetchingCoverage,
    refetch: refetchCoverage,
    error: coverageError,
  } = useQuery({
    queryKey: ['labor-pattern-coverage', patternId, coverageDate],
    queryFn: () => laborScheduleService.checkPatternCoverage(patternId, coverageDate),
    enabled: Boolean(patternId) && Boolean(coverageDate),
  });

  const addMutation = useMutation({
    mutationFn: () => {
      if (!form.skillId) throw new Error('Selecciona una habilidad.');
      if (!form.validFrom) throw new Error('Indica la fecha de inicio de vigencia.');
      return laborScheduleService.addPatternSkillRequirement(patternId, {
        skillId: form.skillId,
        minLevel: form.minLevel,
        requiredCount: form.requiredCount,
        validFrom: form.validFrom,
        validTo: form.validTo || null,
        reason: form.reason,
      });
    },
    onSuccess: async () => {
      setFormError(null);
      setForm((prev) => ({ ...prev, skillId: '', reason: '' }));
      await queryClient.invalidateQueries({ queryKey: ['labor-patterns-with-skills', calendarId] });
      await refetchCoverage();
      onAfterChange?.();
    },
    onError: (err) => {
      setFormError(err?.message || 'No se pudo añadir el requisito.');
    },
  });

  const fullyCovered = isCoverageFullyCovered(coverage);
  const missingSkills = getCoverageMissingSkills(coverage);
  const coverageBusy = loadingCoverage || fetchingCoverage;

  return (
    <div className="mb-4 rounded-lg border border-gray-700 bg-gray-800/80 p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Zap className="w-4 h-4 text-yellow-400 shrink-0" />
        <h3 className="text-sm font-medium text-white">
          Habilidades del patrón: {patternName || `Patrón ${patternId}`}
        </h3>
      </div>

      {/* Requisitos vigentes */}
      <div>
        <p className="text-xs text-gray-400 mb-2">
          Requisitos vigentes el {formatRequirementDate(`${coverageDate}T12:00:00`)}
        </p>
        {loadingPatterns ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando requisitos…
          </div>
        ) : activeRequirements.length === 0 ? (
          <p className="text-sm text-gray-500">
            Sin requisitos de habilidad para esta fecha. Añade uno abajo.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {activeRequirements.map((req) => (
              <div
                key={req.id}
                className="rounded-lg border border-gray-600 bg-gray-700/40 px-3 py-2 text-sm"
              >
                <p className="font-medium text-white">
                  {req.Skill?.name ?? req.skill?.name ?? `Skill #${req.skill_id}`}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Mín. {getLevelLabel(req.min_level)} · {req.required_count} empleado
                  {req.required_count !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatRequirementDate(req.valid_from)}
                  {req.valid_to ? ` → ${formatRequirementDate(req.valid_to)}` : ' → vigente'}
                </p>
                {req.reason && (
                  <p className="text-xs text-gray-400 mt-1 italic">{req.reason}</p>
                )}
              </div>
            ))}
          </div>
        )}
        {allRequirements.length > activeRequirements.length && (
          <p className="text-xs text-gray-500 mt-2">
            {allRequirements.length - activeRequirements.length} requisito(s) fuera de vigencia en esta fecha.
          </p>
        )}
      </div>

      {/* Añadir requisito */}
      <div className="border-t border-gray-700 pt-4">
        <p className="text-xs text-gray-400 mb-3">Añadir requisito de habilidad</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[160px] flex-1 max-w-xs">
            <label className="block text-xs text-gray-400 mb-1">Habilidad *</label>
            <select
              value={form.skillId}
              onChange={(e) => setForm((f) => ({ ...f, skillId: e.target.value }))}
              disabled={loadingCatalog || skillsCatalog.length === 0}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            >
              <option value="">
                {loadingCatalog
                  ? 'Cargando…'
                  : skillsCatalog.length === 0
                    ? 'Sin habilidades en catálogo'
                    : 'Seleccionar…'}
              </option>
              {skillsCatalog.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Nivel mín.</label>
            <select
              value={form.minLevel}
              onChange={(e) => setForm((f) => ({ ...f, minLevel: e.target.value }))}
              className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            >
              {LEVELS.map((l) => (
                <option key={l} value={l}>{getLevelLabel(l)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Cantidad</label>
            <input
              type="number"
              min="1"
              value={form.requiredCount}
              onChange={(e) => setForm((f) => ({ ...f, requiredCount: e.target.value }))}
              className="w-20 bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Vigente desde *</label>
            <input
              type="date"
              value={form.validFrom}
              onChange={(e) => setForm((f) => ({ ...f, validFrom: e.target.value }))}
              className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Hasta (opc.)</label>
            <input
              type="date"
              value={form.validTo}
              onChange={(e) => setForm((f) => ({ ...f, validTo: e.target.value }))}
              min={form.validFrom || undefined}
              className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="min-w-[140px] flex-1 max-w-sm">
            <label className="block text-xs text-gray-400 mb-1">Motivo (opc.)</label>
            <input
              type="text"
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="Ej: nueva máquina CNC"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <button
            type="button"
            onClick={() => {
              setFormError(null);
              addMutation.mutate();
            }}
            disabled={addMutation.isPending || !form.skillId || skillsCatalog.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {addMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Añadir requisito
          </button>
        </div>

        {skillsCatalog.length === 0 && !loadingCatalog && (
          <p className="mt-2 text-xs text-amber-400">
            Crea habilidades en el catálogo (debajo del calendario) antes de definir requisitos.
          </p>
        )}
        {formError && (
          <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {formError}
          </p>
        )}
      </div>

      {/* Cobertura */}
      <div className="border-t border-gray-700 pt-4">
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Comprobar cobertura el</label>
            <input
              type="date"
              value={coverageDate}
              onChange={(e) => setCoverageDate(e.target.value)}
              className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => refetchCoverage()}
            disabled={coverageBusy}
            className="px-3 py-2 text-sm rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
          >
            {coverageBusy ? 'Comprobando…' : 'Actualizar cobertura'}
          </button>
        </div>

        {coverageError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-800 bg-red-900/20 px-3 py-2 text-sm text-red-300">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {coverageError.message || 'Error al comprobar cobertura.'}
          </div>
        )}

        {!coverageError && !coverageBusy && coverage && fullyCovered && (
          <div className="flex items-start gap-2 rounded-lg border border-emerald-800/60 bg-emerald-900/20 px-3 py-2 text-sm text-emerald-300">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Cobertura completa</p>
              <p className="text-xs text-emerald-400/80 mt-0.5">
                Los empleados asignados al patrón cubren todos los requisitos de habilidad vigentes.
              </p>
            </div>
          </div>
        )}

        {!coverageError && !coverageBusy && coverage && !fullyCovered && (
          <div className="flex items-start gap-2 rounded-lg border border-orange-800/60 bg-orange-900/20 px-3 py-2 text-sm text-orange-200">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Infracobertura de habilidades</p>
              {coverage.Available != null && (
                <p className="text-xs text-orange-300/80 mt-0.5">
                  Empleados asignados disponibles: {coverage.Available ?? coverage.available ?? 0}
                </p>
              )}
              {missingSkills.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs">
                  {missingSkills.map((gap, i) => (
                    <li key={i}>· {gap}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {!coverageError && activeRequirements.length === 0 && (
          <p className="text-xs text-gray-500">
            Sin requisitos vigentes: la cobertura se considera completa por defecto.
          </p>
        )}
      </div>
    </div>
  );
}
